import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    Input
} from '@angular/core';
import { OnDestroy } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import * as _ from 'lodash';
import { Subject } from 'rxjs';

@Component({

    selector: 'syn-phone-input',
    templateUrl: 'phone-input.component.html',
    styleUrls: ['phone-input.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: forwardRef(() => PhoneInputComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => PhoneInputComponent), multi: true },
        { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => PhoneInputComponent) }]
})

export class PhoneInputComponent implements ControlValueAccessor, Validator, OnDestroy {

    // used by MatFormFieldControl
    static nextId = 0;

    @HostBinding() id = `my-tel-input-${PhoneInputComponent.nextId++}`;
    @HostBinding('attr.aria-describedby') describedBy = '';

    // let it know when something happens in the form field control
    // that may require the form field to run change detection
    stateChanges = new Subject<void>();

    // used by MatFormFieldControl if we are in an error state
    /// @ts-ignore
    private errorState: boolean;

    // used by the the Control Value Accessor
    // Placeholders for the callbacks which are later provided
    // @ts-ignore
    private onTouchedCallback: Function;
    private onChangeCallback: Function;
    private phoneNumberValue = '';
    private _placeholder: string;
    private _required = false;
    private _disabled = false;
    private focused = false;

    // used by MatFormFieldControl
    // a unique string for the type of control in form field
    // @ts-ignore
    private controlType = 'my-syn-phone-input';

    constructor(private fm: FocusMonitor, private elRef: ElementRef) {
        /**
         * This property indicates whether or not the form field control
         * should be considered to be in a focused state
         */
        this.fm.monitor(elRef.nativeElement, true).subscribe(origin => {
            this.focused = !!origin;
            this.stateChanges.next();
        });
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input()
    get placeholder() {
        return this._placeholder;
    }

    // This property is used to indicate whether the input is required.
    @Input()
    get disabled() {
        return this._disabled;
    }

    // This property is used to indicate whether the input is required.
    @Input()
    get required() {
        return this._required;
    }

    @Input()
    get value() {
        return this.phoneNumberValue;
    }

    // indicates whether the form field control is empty
    get empty(): boolean {
        return _.isEmpty(this.phoneNumberValue);
    }

    set disabled(dis) {
        this._disabled = coerceBooleanProperty(dis);
    }

    set required(req) {
        this._required = coerceBooleanProperty(req);
    }

    set value(tel: string) {
        if (_.isEmpty(tel)) {
            return;
        }
        this.phoneNumberValue = tel;
        this.stateChanges.next();
    }

    set placeholder(plh: string) {
        this._placeholder = plh;
        this.stateChanges.next();
    }

    // used for the aria-describedby attribute of your component
    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    // This method will be called when the form field is clicked on
    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.elRef.nativeElement.querySelector('input').focus();
        }
    }

    // this method is automatically called to know if the form input is valid
    public validate(c: AbstractControl): ValidationErrors | null {
        return this.validatePhoneFormat(c);
    }

    // allow to write programatically the value
    writeValue(phoneInput: string): void {
        if (_.isEmpty(phoneInput)) {
            return;
        }
        this.phoneNumberValue = phoneInput;
        this.stateChanges.next();
    }

    onKeyUp(event: any): void {
        // we always have a value cause our target is an input
        this.phoneNumberValue = event.target.value;
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    // change events from the textarea
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onChange(event) {
        this.phoneNumberValue = this.formatPhoneNumber(event.target.value);
        this.stateChanges.next();
        this.onChangeCallback(this.phoneNumberValue);
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
    }

    public formatPhoneNumber(phoneNumber: string): string {
        // remove spaces and points
        phoneNumber = phoneNumber.replace(/[ .]/g, '');

        if (phoneNumber.startsWith('00')) {
            phoneNumber = phoneNumber.replace('00', '+');
        } else if (phoneNumber.startsWith('0')) {
            phoneNumber = phoneNumber.replace('0', '+33');
        }
        return phoneNumber;
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    private validatePhoneFormat(c: AbstractControl): ValidationErrors | null {
        if (_.isEmpty(c.value)) {
            this.errorState = false;
            this.stateChanges.next();
            return null;
        }

        const REGEXPERRORS: { regexp: RegExp; errorName: string }[]
            = [
                { regexp: /^\+.*/, errorName: 'startWithPlus' }
                , { regexp: /^\+\d*$/, errorName: 'invalidChar' }, { regexp: /^\+\d{4,15}$/, errorName: 'invalidNumberOfChar' }
            ];

        let errorFound: { validPhoneError: string };
        REGEXPERRORS.forEach(regErr => {
            const error = regErr.regexp.test(c.value) ? null : {
                validPhoneError: regErr.errorName
            };
            if (error) {
                errorFound = error;
                // break forEach by returning false
                return false;
            }
        });

        this.errorState = errorFound ? true : false;
        return errorFound ? errorFound : null;
    }
}
